import React, { useCallback, useEffect } from "react";
import ProductCard from "@components/card/ProductCard";
import styled from "@emotion/styled";
import { useInfiniteQuery } from "@tanstack/react-query";
import { NEW_IN_URL } from "@libs/apiUrl";
import { LOAD_SIZE_10 } from "@libs/reactQuery/queryClient";
import useIntersection from "@hook/useIntersection";
import useAxios from "@libs/axios/useAxios";
import useGenderStore from "@store/header/useGenderStore";
import { toPrice } from "@utils/functions";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1.6rem",
});

const Title = styled.h3(({ theme }) => ({
  color: theme.color.black,
  fontSize: "2rem",
  fontWeight: 600,
  margin: 0,
  lineHeight: "2rem",
}));

const ListContainer = styled.div(
  () =>
    ({
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
    }) as const,
);

const NewInSwiper = () => {
  const { axiosInstance } = useAxios();
  const { gender } = useGenderStore();
  const lang = window.localStorage.getItem("language");
  const categoryId = gender === "men" ? 1002 : 1001;

  const getNewInList = useCallback(
    async ({ pageParam = 1 }) => {
      let url = `${NEW_IN_URL}?page=${pageParam}&limit=${LOAD_SIZE_10}&category_id=${categoryId}`;

      const res = await axiosInstance.get(url);

      if (res.status === 200) {
        return res.data;
      }
    },
    [axiosInstance, categoryId],
  );

  const {
    data: newInList,
    fetchNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["newInList", gender],
    queryFn: ({ pageParam = 1 }) => getNewInList({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPages, pages) =>
      pages?.length < lastPages.data.total_count / LOAD_SIZE_10
        ? pages.length + 1
        : undefined,
  });

  const { setObserverTarget } = useIntersection(() => {
    fetchNextPage();
  });

  return (
    <Container>
      <Title>NEW IN</Title>
      <ListContainer>
        {newInList?.pages?.map((page) => {
          return page?.data?.list?.map((product: any) => {
            const brand =
              product[`brand_name_${lang}`] || product.brand_name_ko;
            const name =
              product[`product_name_${lang}`] || product.product_name_ko;

            return (
              <ProductCard
                key={product.product_id}
                id={product.product_id}
                brand={brand}
                name={name}
                src={product.thumbnail_url}
                price={product.price}
                likeCount={product.like_total}
                chatCount={product.qna_total}
                soldout={product.is_sold_out === 1}
                isLiked={product.is_liked}
                condition={product.product_condition}
                isActive={product.is_active ? false : true}
              />
            );
          });
        })}
      </ListContainer>
      <div ref={setObserverTarget} />
    </Container>
  );
};

export default NewInSwiper;
